/* global google */

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { GoogleMap, LoadScriptNext, InfoWindow, StandaloneSearchBox } from '@react-google-maps/api';
import { useRestaurants } from '../../../contexts/RestaurantsContext';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import './Map.css';

const center = {
  lat: 33.7490,
  lng: -84.3880,
};

const libraries = ['places', 'marker', 'geometry'];

const Map = () => {
  const { restaurants } = useRestaurants();
  const { restaurantName } = useParams(); // Get restaurant name from the URL

  // Memoize currentRestaurant to avoid re-render issues
  const currentRestaurant = useMemo(
    () => restaurants[restaurantName]?.locations || [],
    [restaurants, restaurantName]
  );

  const [searchedLocation, setSearchedLocation] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [sortedLocations, setSortedLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null); // Add this state to track selected location
  const [searchMarkerInfo, setSearchMarkerInfo] = useState(null); // State to track the searched location marker info window
  const [load, setLoad] = useState(false);
  const currentMarkers = useRef([]);
  const searchBoxRef = useRef(null);
  const mapRef = useRef(null);
  const userMarkerRef = useRef(null);
  const searchMarkerRef = useRef(null); // Ref to store the searched location marker

  // Sort locations by distance from the center
  const sortLocationsByDistance = useCallback(
    (center) => {
      // console.log("me" + Object.toString(center))
      // console.log("hi" + currentRestaurant)
      if (window.google) {
        const distances = currentRestaurant.map((location) => {
          const distance = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(center.lat, center.lng),
            new google.maps.LatLng(location.lat, location.lng)
          );
          return { ...location, distance };
        });
              // Filter locations within 200 miles (200 miles ≈ 321869 meters)
        const nearbyLocations = distances.filter((location) => location.distance <= 321869111111);
        console.log("Nearby locations:", nearbyLocations);
        nearbyLocations.sort((a, b) => a.distance - b.distance);
        setSortedLocations(nearbyLocations);
      }
    },
    [currentRestaurant]
  );

// Handle map load and initial marker setup
const onLoad = useCallback(async (mapInstance) => {
  mapRef.current = mapInstance;
  // console.log("hi")
  // console.log(window.google)
  if (window.google) {
    try {
      const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');

      // Add user location marker
      if (userLocation) {
        userMarkerRef.current = new google.maps.Marker({
          position: userLocation,
          map: mapInstance,
          title: 'Your Location',
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: 'blue',
            fillOpacity: 1,
            strokeColor: 'white',
            strokeWeight: 2,
            scale: 10,
            mapId: '7dec7c6de44bfea1',
          },
        });
        console.log("hi true")
        sortLocationsByDistance(userLocation)
        setLoad(true)
      }
    } catch (error) {
      console.error('Error loading marker library:', error);
    }
  } else {
    console.error('Google Maps API not loaded');
  }
}, [userLocation]);

useEffect(() => {
  if (mapRef.current && window.google) {
    const updateMarkers = async () => {
      try {
        const { AdvancedMarkerElement } = await google.maps.importLibrary('marker');

        // Center point - User location or default location
        const center = searchedLocation != null ? searchedLocation : { lat: 33.7490, lng: -84.3880 };
        console.log(center)
        console.log(sortedLocations)


        // Clear existing markers
        currentMarkers.current.forEach(marker => marker.setMap(null));
        currentMarkers.current = []; // Reset the array after clearing

        // Update user location marker
        if (userMarkerRef.current) {
          userMarkerRef.current.setPosition(userLocation);
        }

        console.log(sortedLocations)
        // Add new markers based on sortedLocations
        sortedLocations.forEach((location) => {
          console.log(location)
          const distance = google.maps.geometry.spherical.computeDistanceBetween(
            new google.maps.LatLng(center.lat, center.lng),
            new google.maps.LatLng(location.lat, location.lng)
          );
          console.log(distance)

          if (distance <= 32186911111111) { // Ensure proper distance limit (200 miles)
            const markerElement = new AdvancedMarkerElement({
              position: { lat: location.lat, lng: location.lng },
              map: mapRef.current,
              title: location.name || restaurantName,
            });

            markerElement.addListener('click', () => {
              setSelectedRestaurant({
                position: { lat: location.lat, lng: location.lng },
                address: location.address,
              });
            });

            currentMarkers.current.push(markerElement); // Store the marker in the array
          }
        });
      } catch (error) {
        console.error('Error updating markers:', error);
      }
    };

    updateMarkers();
  }
}, [sortedLocations, load]);
  

  // Handle search box place changes
  const onPlacesChanged = () => {
    if (searchBoxRef.current && window.google) {
      const places = searchBoxRef.current.getPlaces();
      if (places && places.length > 0) {
        // console.log(places); // Debugging
        const location = places[0].geometry?.location;
        if (location) {
          // console.log("Lat:", location.lat(), "Lng:", location.lng());
        } else {
          console.error("No geometry data found for the place.");
        }
      } else {
        console.error("No places returned.");
      }
      const place = places[0];

      if (place.address_components && place.address_components.some(component => component.short_name === 'US')) {
        const newCenter = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        if (mapRef.current) {
          mapRef.current.panTo(newCenter);
        }
        setSearchedLocation(newCenter)
        sortLocationsByDistance(newCenter);

        // Add or update the searched location marker
        if (searchMarkerRef.current) {
          searchMarkerRef.current.setPosition(newCenter);
          // console.log(searchMarkerRef);
          // console.log(searchMarkerRef.current);
        } else {
          searchMarkerRef.current = new google.maps.Marker({
            position: newCenter,
            map: mapRef.current,
            title: 'Searched Location',
            icon: {
              path: google.maps.SymbolPath.CIRCLE, //todo
              fillColor: 'blue',
              fillOpacity: 1,
              strokeColor: 'blue',
              strokeWeight: 2,
              scale: 5,
              mapId: '7dec7c6de44bfea1',
            },
          }
          );
          //todo
          
          // searchMarkerRef.current.addListener('click', () => {
          //   setSearchMarkerInfo({
          //     position: newCenter,
          //     address: place.formatted_address,
          //   });
          // });
        }

        // Show the info window immediately upon search
          // setSearchMarkerInfo({
          //   position: newCenter,
          //   address: place.formatted_address,
          // });
      }
    }
  };

  // Request user's location when the component mounts
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLatLng = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setUserLocation(userLatLng);
          if (mapRef.current) {
            mapRef.current.panTo(userLatLng);

            // Update or add the user location marker
            if (userMarkerRef.current) {
              userMarkerRef.current.setPosition(userLatLng);
            } else {
              userMarkerRef.current = new google.maps.Marker({
                position: userLatLng,
                map: mapRef.current,
                title: 'Your Location',
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  fillColor: 'blue',
                  fillOpacity: 1,
                  strokeColor: 'white',
                  strokeWeight: 2,
                  scale: 10,
                  mapId: '7dec7c6de44bfea1',
                },
              });
            }
          }
          sortLocationsByDistance(userLatLng);
        },
        (error) => {
          // console.error('Error getting user location:', error);
          const userLatLng = {
            lat: 33.7490,
            lng: -84.3880,
          };
          setUserLocation(userLatLng);
          if (mapRef.current) {
            mapRef.current.panTo(userLatLng);

            // Update or add the user location marker
            if (userMarkerRef.current) {
              userMarkerRef.current.setPosition(userLatLng);
            } else {
              userMarkerRef.current = new google.maps.Marker({
                position: userLatLng,
                map: mapRef.current,
                title: 'Your Location',
                icon: {
                  path: google.maps.SymbolPath.CIRCLE,
                  fillColor: 'blue',
                  fillOpacity: 1,
                  strokeColor: 'white',
                  strokeWeight: 2,
                  scale: 10,
                  mapId: '7dec7c6de44bfea1',
                },
              });
            }
          }
          sortLocationsByDistance(userLatLng);
        }
      );
    }
  }, [sortLocationsByDistance]);

  // Function to center the map to a specific location
  const handleLocationClick = (location) => {
    if (mapRef.current) {
      const newCenter = { lat: location.lat, lng: location.lng };
      mapRef.current.panTo(newCenter);
      setSelectedRestaurant({
        position: newCenter,
        address: location.address,
      });
      setSelectedLocationId(location.name); // Set selected location
    }
  };

/* Helper function to find location name */
const findLocationName = (selectedAddress) => {
  // Iterate over each restaurant
  for (const restaurantKey in restaurants) {
    const restaurant = restaurants[restaurantKey];
    
    // Check if the restaurant has locations
    if (restaurant?.locations?.length > 0) {
      // Iterate over the locations
      for (const location of restaurant.locations) {
        // Compare the location address with selectedRestaurant.address
        if (location.address === selectedAddress) {
          return location.name; // Return the matching location name
        }
      }
    }
  }
  return "Location name not found"; // Return a fallback if no match is found
}

const searchOptions = {
  bounds: {
    north: 49.384358, // Northern boundary of the USA
    south: 24.396308, // Southern boundary of the USA
    east: -66.93457, // Eastern boundary of the USA
    west: -125.00165, // Western boundary of the USA
  },
  componentRestrictions: { country: 'us' }, // Restrict to the USA
  types: ['address'], // Restrict to addresses only
};

  return (
    <div className="map-page">
      <LoadScriptNext
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
        libraries={libraries}
      >
        <div className="list-container">
          <div className="search-container">
            <h2>{restaurants[restaurantName].name} Near You</h2>
            <StandaloneSearchBox
              onLoad={(ref) => (searchBoxRef.current = ref)}
              onPlacesChanged={onPlacesChanged}
              options={ searchOptions } // Restrict to USA
            >
              <input
                type="text"
                placeholder="Search for your location"
                className="search-input"
              />
            </StandaloneSearchBox>
          </div>
          <div className="nearbylocations-container">
            <h2>Nearby</h2>
            <ul>
              {sortedLocations.length > 0 ? (
                sortedLocations.map((location) => (
                  <li
                    key={location.name}
                    className={selectedLocationId === location.name ? 'active' : ''}
                    onClick={() => handleLocationClick(location)}
                  >
                    <h3>{location.name}</h3>
                    <p>{location.address}</p>
                    <p className="nearbylocations-distance">
                      {Math.round(location.distance / 1609.34)} miles away
                    </p>
                      <Link
                        className={selectedLocationId === location.name ? 'active' : ''}
                        to={`/restaurants/${restaurantName}/menu`}
                      >
                        Browse Menu
                      </Link>
                      {location.doordash && (
                        <Link
                          className={selectedLocationId === location.name ? 'active' : ''}
                          to={location.doordash}
                        >
                          Order Online
                        </Link>
                      )}
                  </li>
                ))
              ) : (
                <p className="nearbylocations-false">No nearby locations found.</p>
              )}
            </ul>
          </div>
        </div>
        <div className="map-container">
          <GoogleMap
            mapContainerStyle={{ width: '100%', height: '100%' }}
            center={userLocation || center}
            zoom={9}
            onLoad={onLoad}
            options={{
              mapId: '7dec7c6de44bfea1',
              mapTypeControl: false,
              fullscreenControl: false,
              streetViewControl: false,
            }}
          >
            {selectedRestaurant && (
              <InfoWindow className="map-selectedInfo"
                position={selectedRestaurant.position}
                onCloseClick={() => setSelectedRestaurant(null)}
              >
                <div className="map-selectedInfo-box">
                  <h3>{findLocationName(selectedRestaurant.address)}</h3>
                  <p>{selectedRestaurant.address}</p>
                </div>
              </InfoWindow>
            )}
            {searchMarkerInfo && (
              <InfoWindow className="map-searchedInfo"
                position={searchMarkerInfo.position}
                onCloseClick={() => setSearchMarkerInfo(null)}
              >
                <div className="map-searchedInfo-box">
                  <h3>{searchMarkerInfo.address}</h3>
                  {/* <h3>{console.log(searchMarkerInfo)}</h3>
                  <h3>{console.log(searchBoxRef)}</h3>
                  <h3>{console.log(searchMarkerRef)}</h3> */}
                  {/* <h4>Searched Location</h4> */}
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </div>
      </LoadScriptNext>
    </div>
  );
};

export default Map;
